import type { CartItem } from "@server/gql/graphql";
import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";

type CartItemContextProps = {
  locale: string;
  item?: CartItem;
  loading: boolean;
  removeItem: (item: CartItem) => void;
  updateItem: (item: CartItem) => void;
  hideStockStatus?: boolean;
};

export const CartItemContext = createContext<CartItemContextProps>({
  locale: "",
  loading: false,
  removeItem: () => Promise.resolve(),
  updateItem: () => Promise.resolve(),
  hideStockStatus: false,
});

export type CartItemProviderProps = CartItemContextProps & {
  children: ReactNode;
  className?: string;
};

export const CartItemProvider = ({
  children,
  locale,
  item,
  removeItem,
  updateItem,
  loading,
}: CartItemProviderProps) => {
  const hideStockStatus = useMemo(() => {
    return (
      item?.stockStatus?.statusCode === "DeliveryItem" &&
      item.stockStatus.nextDelivery?.quantity === 0
    );
  }, [item?.stockStatus]);

  const value = useMemo(
    () => ({
      locale,
      item,
      removeItem,
      updateItem,
      loading,
      hideStockStatus,
    }),
    [
      locale,
      item,
      removeItem,
      updateItem,
      loading,
      hideStockStatus,
    ],
  );

  return (
    <CartItemContext.Provider value={value}>
      {children}
    </CartItemContext.Provider>
  );
};

export const useCartItemContext = () => {
  const context = useContext(CartItemContext);

  if (!context) {
    throw new Error(
      "useCartItemContext must be used within a CartItemProvider",
    );
  }

  return context;
};
